import { computed, reactive, ref, unref } from 'vue';
import { defineStore } from 'pinia';
import { SignUpStores } from '@/stores/modules/signup/index';
import {
    processFindNBNLocation,
    loadAddress,
    processPerformSimpleNbnSq,
    getAvailablePackages as loadAvailablePackages,
    signUpLogin as handleSignUpLogin,
    accountCreation,
    initiateOrder as initOrder,
    getAllBanks,
    getTermsAndConditions as getTAC,
    paymentMethodBankAccount,
    paymentMethodCreditCardAccount,
    performPayment,
    setServiceConfigVocusNbn,
    completeOrder,
} from '@/modules/Signup/services/signupService';
import { SEARCH_RESULT_MESSAGES } from '@/modules/Signup/constants/searchResultMessages';
import { cloneDeep } from '@/utilities/dataUtils';

export const useSignUpModuleStore = defineStore(
    SignUpStores.SIGN_UP,
    () => {
        /**
         * Explicitly set the initial value and its fields.
         */
        const initialLocationEligibility = {
            isEligible: null,
            proceedTo: '',
        };

        /**
         * States
         */
        const parentAccountId = ref('');
        const userId = ref(null);

        /** Search */
        const
            address = ref(''),
            optAddresses = ref([]),
            selectedAddressId = ref(null),
            selectedAddressDto = ref({}),
            // Centralize a result message for search
            searchResultTextLoader = ref(''),
            searchResultMessage = ref(''),
            /** Flag that determines is the search result is for alternative technology connection */
            searchResultIsAlternativeTechnology = ref(false);

        const locationEligibility = ref({ ...initialLocationEligibility });
        const
            optNbnLocations = ref({}),
            selectedNbnLocation = ref(null);
        const
            optOrderingOptions = ref({}),
            serviceQualificationResponse = ref({}),
            selectedOrderingOption = ref('');

        const customerAccountId = ref(null);
        const accountPresaleId = ref(null);
        const accountPresaleSq = ref(null);

        const
            packagePlans = ref([]),
            packagePlanDetails = ref({}),
            availableProducts = ref([]);

        const
            /**
             * @deprecated: should be replaced by `selectedPlan` instead
             * @todo: find remnants and remove accordingly
             */
            selectedPlanId = ref(null),
            selectedPlan = ref({}),
            /**
             * @deprecated: should be replaced by `selectedProducts` instead
             * @todo: find remnants and remove accordingly
             */
            selectedModemId = ref(null),
            selectedProducts = ref({});

        /** This will hold the response from the account/store */
        const serviceContactRequirementsDTO = ref({});

        /**
         * Forms
         */
        /** ---- Details */
            // Determines whether the user selected to use the "Use Contact" option.
        const hasUseSiteContactDetails = ref(true);
        const accountCreationResponseDto = ref({});

        /** ---- Activation Process (AVC) */
        const avcDto = reactive({
            avcId: '',
            ntdId: null,    // boolean
        });

        /** ---- Payments methods */
        const optPaymentMethods = ref([]);
        const banks = ref([]);
        /** Selected payment is recurring or not. @default: true */
        const isPaymentRecurring = ref(true);
        const paymentForms = reactive({
            upfront: {
                selectedPaymentMethod: null,
                // Bank Account
                bankId: '',
                bankAccountName: '',
                bankAccountNumber: '',
                bankBsb: '',
                // Credit Card
                ccCardType: '',
                ccCardNumber: '',
                ccCardName: '',
                ccCvv: '',
                ccExpiryDate: '',
            },
            recurring: {
                selectedPaymentMethod: null,
                // Bank Account
                bankId: '',
                bankAccountName: '',
                bankAccountNumber: '',
                bankBsb: '',
                // Credit Card
                ccCardType: '',
                ccCardNumber: '',
                ccCardName: '',
                ccCvv: '',
                ccExpiryDate: '',
            },
        });
        // Contains the response from the payment method API
        const paymentMethodResponseDtp = ref({});

        const termsAndConditions = ref([]);
        const agreements = ref({});

        /**
         * Getters
         */
        const showMultipleNBNLocations = computed(() => {
            return Object.keys(optNbnLocations.value).length > 0;
        });
        const showOrderOptions = computed(() => {
            return Object.keys(optOrderingOptions.value).length > 0;
        });
        const showPackagePlans = computed(() => {
            return (
                Object.keys(serviceQualificationResponse.value).length > 0 &&
                serviceQualificationResponse.value.Result !== 'REJECTED'
            );
        });
        const orderSummary = computed(() => {
            // eslint-disable-next-line no-unused-vars
            const { AvailableProducts, ...otherPlanData } = unref(selectedPlan);

            // Selected products amount
            const productAmount = parseFloat(selectedProducts.value.Price.Amount);

            return {
                ...otherPlanData,
                products: selectedProducts.value,
                // computed
                monthlyTotal: otherPlanData.amount,
                totalDueToday: otherPlanData.amount + productAmount,
            };
        });

        /**
         * Actions
         */
        const setSearchResultTextLoader = (msg) => searchResultTextLoader.value = msg;

        /**
         * Process sign-up login
         * @returns {Promise<{operation: ((function(): Promise<void>)|*), loadingText: string}>}
         */
        async function signUpLogin() {
            try {
                const res = await handleSignUpLogin();
                if (res.success) {
                    parentAccountId.value = res.parentAccountId;
                    // Set userId from the response
                    if (res.tokenObj && res.tokenObj.user_id) {
                        userId.value = res.tokenObj.user_id;
                    } else {
                        console.warn('res.tokenObj or res.tokenObj.user_id is not set');
                    }
                } else {
                    console.warn('handleSignUpLogin failed:', res.message);
                }
            } catch (error) {
                console.error('Error in signUpLogin:', error);
            }
        }

        /**
         * @param keyword
         * @param signal    Abort signal
         * @returns {Promise<void>}
         */
        async function getAddress(keyword, signal) {
            const requestPayload = {
                address: keyword,
                country_id: 113, // Statically set to 113 = Australia
            };

            try {
                const res = await loadAddress(requestPayload, signal);

                // Check if the request was aborted before updating the state
                if (signal.aborted) return;

                if (res.success) optAddresses.value = res.data;
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
                throw error;
            }
        }

        async function checkLocationEligibility() {
            setSearchResultTextLoader('Finding NBN Location ...');

            // Reset
            optAddresses.value = [];
            optOrderingOptions.value = {};
            clearLocationEligibility();

            try {
                const res = await processFindNBNLocation(unref(selectedAddressDto));
                if (!res.success) {
                    locationEligibility.value.isEligible = false;
                    return;
                }

                const
                    nbnLocations = res.nbnLocations || {},
                    locationKeys = Object.keys(nbnLocations);

                selectedAddressId.value = res.addressId;

                /**
                 * Determine the processing approach based on the number of LOC:
                 * - if it's > 1, then it will first ask the user to select the LOC based from the options
                 * - otherwise auto process, with that option being pre-selected
                 */
                if (locationKeys.length > 1) {
                    // Handle the option selection to the UI
                    optNbnLocations.value = nbnLocations;
                } else {
                    selectedNbnLocation.value = locationKeys[0];
                    await processOrderingOptions();
                }
            } catch (error) {
                console.error(error);
                locationEligibility.value.isEligible = false;
            }
        }

        function clearLocationEligibility() {
            Object.assign(locationEligibility.value, initialLocationEligibility);
        }

        /**
         * If the location selected is eligible, it should perform the following:
         * - perform simple nbn sq
         * - open a modal displaying the available ordering options. However, if there's only one option available,
         *   skip the modal and automatically select it, then proceed to show the available packages.
         *
         * @returns {Promise<void>}
         */
        async function processOrderingOptions() {
            setSearchResultTextLoader('Performing NBN Service Qualification ...');

            const requestPayload = {
                DirectoryID: selectedNbnLocation.value,
            };

            try {
                const res = await processPerformSimpleNbnSq(requestPayload);
                if (res.success) {
                    optOrderingOptions.value = res.orderingOptions;
                    serviceQualificationResponse.value = res.sqResponse;
                    locationEligibility.value.isEligible = true;

                    let overrideSearchResultMessage;

                    // Check if connection is eligible AND if isAlternativeTechnology is true, set a separate message
                    if (res.sqResponse.AlternativeTechnology === 'TRUE') {
                        overrideSearchResultMessage = SEARCH_RESULT_MESSAGES.alternativeTechnology;
                        searchResultIsAlternativeTechnology.value = true;
                    } else if (res.sqResponse.AlternativeTechnology === 'FALSE') {
                        overrideSearchResultMessage = SEARCH_RESULT_MESSAGES.eligible;
                        searchResultIsAlternativeTechnology.value = false;
                    }

                    overrideSearchResultMessage.detail = overrideSearchResultMessage.detail.replace(
                        '{connection}',
                        res.sqResponse.ServiceType,
                    );

                    searchResultMessage.value = overrideSearchResultMessage;
                } else {
                    locationEligibility.value.isEligible = false;
                    searchResultMessage.value = {
                        title: SEARCH_RESULT_MESSAGES.ineligible.title,
                        detail: res.message,
                        severity: SEARCH_RESULT_MESSAGES.ineligible.severity,
                    };
                    searchResultIsAlternativeTechnology.value = false;
                }
            } catch (error) {
                console.error(error);
            } finally {
                // Reset
                searchResultTextLoader.value = '';
                optNbnLocations.value = {};
            }
        }

        async function getAvailablePackages() {
            const loadingText = 'Loading available packages ...';

            const operation = async () => {
                const requestPayload = {
                    ParentAccountID: parentAccountId.value,
                    SQ_Result: serviceQualificationResponse.value,
                };

                try {
                    const res = await loadAvailablePackages(requestPayload);
                    if (res.success) {
                        packagePlans.value = res.packagePlans;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    // Reset
                    optOrderingOptions.value = {};
                }
            };

            return {
                operation,
                loadingText,
            };
        }

        /**
         * @param payload
         * @returns {Promise<void>}
         */
        async function processAccountCreation(payload) {
            const loadingText = 'Creating an account. Please wait ...';

            const operation = async () => {
                // Some fields are not currently used but kept for future implementation
                const {
                    firstname,
                    lastname,
                    email,
                    phone,
                    dob,
                    billing_address,
                    custom_billing_address,
                    shipping_method,
                    shipping_address,
                    custom_shipping_address,
                    has_existing_connection,
                } = payload;

                let billingAddress = billing_address;
                if (custom_billing_address) billingAddress = custom_billing_address;

                // Not yet implemented
                let shippingAddress = shipping_address;
                if (custom_shipping_address) shippingAddress = custom_shipping_address;

                // TODO: update accordingly. Currently getting an error.
                const requestPayloadDTO = {
                    firstname,
                    lastname,
                    email,
                    phone,
                    dob,
                    billingAddress,
                    shippingAddress,
                    shipping_method,
                    has_existing_connection,
                    userId: userId.value, // Pass the userId to accountCreation
                };

                try {
                    const res = await accountCreation(requestPayloadDTO);
                    if (res.success) {
                        customerAccountId.value = res.data.customerAccountId;
                        // TODO: refactor this accordingly, for now just have it this way. Put this on service.
                        // This is tailored for the service contact requirements request payload
                        const contactCol = res.data.contactCollections;
                        const
                            addressDtl = contactCol.physical_address,
                            contactDtl = contactCol,
                            phoneDtl = contactCol.primary_phone,
                            emailDtl = contactCol.primary_email;

                        // Contains the response from the account creation API
                        accountCreationResponseDto.value = res.data;

                        // Prepare the request payload for the service contact, which will then be used after the
                        // initiate-order API is called.
                        serviceContactRequirementsDTO.value = {
                            address: {
                                lotNumber: addressDtl.street_lot_number,
                                streetNumberStart: addressDtl.street_number_start,
                                streetNumberEnd: addressDtl.street_number_end,
                                streetName: addressDtl.street_name,
                                streetType: addressDtl.address_street_type.name,
                                suburb: addressDtl.suburb,
                                state: addressDtl.address_physical_state.name,
                                postcode: addressDtl.postcode,
                                country: addressDtl.country.name,
                            },
                            details: {
                                type: 'End User',
                                title: 'MR', // TODO: no `title` data on account/store endpoint
                                firstName: contactDtl.firstname,
                                lastName: contactDtl.lastname,
                                dob: '1999-01-02', // TODO: no `dob` data on account/store endpoint
                                contactType: 'Site Contact',
                            },
                            phone: {
                                type: phoneDtl.phoneNumberType.name,
                                number: phoneDtl.phonenumber,
                            },
                            email: {
                                type: 'Business', // TODO: no data
                                address: emailDtl.address,
                            },
                        };
                        return true;
                    }
                    return false;
                } catch (error) {
                    console.error('Error in processAccountCreation:', error);
                    return false;
                }
            };

            return {
                operation,
                loadingText,
            };
        }

        async function processInitiateOrder() {
            const loadingText = 'Initiating order. Please wait ...';

            const operation = async () => {
                const requestPayload = {
                    customerAccountId: customerAccountId.value,
                    parentAccountId: parentAccountId.value,
                    productIds: [2040943], // TODO: not extracted yet, this is just a test
                    packagePlanId: selectedPlanId.value,
                    directoryId: selectedNbnLocation.value,
                    avcId: avcDto.avcId,
                    ntdId: avcDto.ntdId,
                    orderingOption: selectedOrderingOption.value,
                };
                try {
                    const res = await initOrder(requestPayload);

                    if (res.error) return false;

                    accountPresaleId.value = res.data.accountPresale;
                    accountPresaleSq.value = res.data.accountPresaleSQ;

                    // Process the service contact requirements
                    await setServiceConfigVocusNbn({
                        accountPresaleSq: accountPresaleSq.value,
                        username: customerAccountId.value,
                        addressId: accountCreationResponseDto.value.contactCollections.physical_address.id,
                        contactCollection: serviceContactRequirementsDTO.value,
                    });
                    return true;
                } catch (error) {
                    console.error(error);
                    return false;
                }
            };

            return {
                operation,
                loadingText,
            };
        }

        // TODO: update accordingly
        function getPaymentMethods() {
            optPaymentMethods.value = [
                { id: 1, name: 'Bank Account' },
                { id: 10, name: 'Credit Card' },
            ];
        }

        async function getBanks() {
            try {
                const res = await getAllBanks();
                banks.value = res.data;
            } catch (error) {
                console.error(error);
            }
        }

        function updatePaymentForm(formType, field, value) {
            if (paymentForms[formType]) {
                paymentForms[formType][field] = value;
            }
        }

        function syncRecurringWithUpfront() {
            paymentForms.recurring = { ...paymentForms.upfront };
        }

        function clearPaymentForms() {
            Object.keys(paymentForms).forEach(key => {
                Object.keys(paymentForms[key]).forEach(field => {
                    paymentForms[key][field] = '';
                });
                paymentForms[key].selectedPaymentMethod = null;
            });
        }

        async function processSelectedPaymentMethod() {
            const loadingText = 'Saving payment method. Please wait ...';

            /**
             * Logic:
             *
             * - if the `isPaymentRecurring` is true, then it will use the payment from the upfront form
             * - if the `isPaymentRecurring` is false, then it will use the payment from the recurring form (modal)
             * -- get the recurring form data and pass it
             * -- should store the upfront form as well as this will be passed to the `perform-payment` API
             *
             * - the form (the form being used for the recurring) being used will then trigger the following APIs in
             * sequence:
             * -- get automated payment info
             * ------ no request payload
             * -- update automated payment info
             * ------ use the response payload from the "get automated payment info"
             *
             * During the last step, i.e., after the terms and conditions are accepted, it should trigger the
             * "perform payment" API, which will get the upfront payment information.
             */
            const operation = async () => {
                const activeFormType = isPaymentRecurring.value ? 'upfront' : 'recurring';
                const activeFormData = paymentForms[activeFormType];

                // bank account or cc
                const selectedPaymentMethod = activeFormData.selectedPaymentMethod;

                if (!selectedPaymentMethod) {
                    throw new Error('No payment method selected');
                }

                const PAYMENT_METHOD_FIELDS = {
                    1: ['bankId', 'bankAccountName', 'bankAccountNumber', 'bankBsb'],
                    2: ['ccCardType', 'ccCardNumber', 'ccCardName', 'ccCvv', 'ccExpiryDate'],
                };

                const requiredFields = PAYMENT_METHOD_FIELDS[selectedPaymentMethod.id];
                if (!requiredFields || requiredFields.length === 0) {
                    throw new Error('Invalid payment method');
                }

                // Validate required fields
                const missingFields = requiredFields.filter(field => !activeFormData[field]);
                if (missingFields.length > 0) {
                    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
                }

                // Clone upfront data to recurring ONLY if isPaymentRecurring is true
                if (isPaymentRecurring.value) {
                    paymentForms.recurring = cloneDeep(paymentForms.upfront);
                }

                // Extract only the relevant fields from both forms
                // TODO:
                //  I think if we established the thought that ONLY the recurring part should be save to the
                //  payment method, then this conditions is unnecessary. Clarify further.
                const payload = {
                    upfront: Object.fromEntries(
                        requiredFields.map(field => [field, paymentForms.upfront[field]]),
                    ),
                    recurring: Object.fromEntries(
                        requiredFields.map(field => [field, paymentForms.recurring[field]]),
                    ),
                };

                let resPaymentMethod;

                if (selectedPaymentMethod.id === 1) {
                    const resBankAccount = await paymentMethodBankAccount(
                        customerAccountId.value,
                        payload,
                        isPaymentRecurring.value,
                    );

                    if (resBankAccount.success) {
                        resPaymentMethod = resBankAccount.data;
                    } else {
                        throw new Error('Failed to save bank account');
                    }
                } else if (selectedPaymentMethod.id === 2) {
                    const resCc = await paymentMethodCreditCardAccount(
                        customerAccountId.value,
                        payload,
                        isPaymentRecurring.value,
                    );

                    if (resCc.success) {
                        resPaymentMethod = resCc.data;
                    } else {
                        throw new Error('Failed to save credit card');
                    }
                }

                paymentMethodResponseDtp.value = resPaymentMethod;
            };

            return {
                operation,
                loadingText,
            };
        }

        async function getTermsAndConditions() {
            const requestPayload = {
                packagePlanId: selectedPlanId.value,
                accountId: parentAccountId.value,
            };

            try {
                const res = await getTAC(requestPayload);
                if (res.success) {
                    termsAndConditions.value = res.data;
                    // Initialize agreements with all terms set to false
                    initializeAgreements();
                }
            } catch (error) {
                console.error(error);
            }
        }

        function initializeAgreements() {
            const agreementsObj = {};
            termsAndConditions.value.forEach(tac => {
                agreementsObj[tac.term_and_condition_id] = false;
            });
            agreements.value = agreementsObj;
        }

        function setAgreement(termId, value) {
            agreements.value[termId] = value;
        }

        // TODO: add API here
        // TODO: subject for removal, check for remnants
        async function register(payload) {
            const requestPayload = {
                ...payload,
                ...{
                    address: address.value,
                    plan_id: selectedPlanId.value,
                    modem_id: selectedModemId.value,
                },
            };
            console.log({ requestPayload });

            /** Clear state after successful registration */
            clear();
        }

        function clear() {
            address.value = '';
            optAddresses.value = [];

            selectedPlanId.value = null;
            selectedModemId.value = null;
        }

        async function processPerformPayment() {
            // TODO: create state for this, some part, I'm not sure how to do yet since the payment method API, is
            // getting some issue with the current env.
            const requestPayload = {
                paymentMethod: paymentForms.upfront.selectedPaymentMethod,
                paymentMethodId: paymentMethodResponseDtp.value.id,
                price: orderSummary.value.monthlyTotal,
                amount: orderSummary.value.totalDueToday,
            };

            try {
                const { success } = await performPayment(customerAccountId.value, requestPayload);
                if (success) {
                    await processCompleteOrder();
                } else {
                    console.error('Payment failed');
                    // Redirect to payment page
                }
            } catch (error) {
                console.error(error);
            }
        }

        async function processCompleteOrder() {
            try {
                await completeOrder(customerAccountId.value, {
                    accountPresaleId: accountPresaleId.value,
                });
            } catch (error) {
                console.error(error);
            }
        }

        return {
            /**
             * States
             */
            parentAccountId,
            userId,

            /** search */
            address,
            optAddresses,
            selectedAddressId,
            selectedAddressDto,
            searchResultTextLoader,
            searchResultMessage,
            searchResultIsAlternativeTechnology,
            locationEligibility,
            optNbnLocations,
            selectedNbnLocation,
            optOrderingOptions,
            serviceQualificationResponse,
            selectedOrderingOption,

            selectedPlanId,
            selectedModemId,
            selectedPlan,
            selectedProducts,
            customerAccountId,
            accountPresaleId,
            accountPresaleSq,
            packagePlans,
            packagePlanDetails,
            availableProducts,

            serviceContactRequirementsDTO,

            /**
             * Forms
             */
            /** Details*/
            hasUseSiteContactDetails,
            optPaymentMethods,

            /** Activation Process (AVC) */
            avcDto,

            /** Payment methods */
            isPaymentRecurring,
            banks,
            paymentForms,

            termsAndConditions,
            agreements,

            /**
             * Getters
             */
            showMultipleNBNLocations,
            showOrderOptions,
            showPackagePlans,
            orderSummary,

            /**
             * Actions
             */
            signUpLogin,
            /** search */
            getAddress,
            checkLocationEligibility,
            setSearchResultTextLoader,
            clearLocationEligibility,
            processOrderingOptions,

            getAvailablePackages,
            processAccountCreation,
            processInitiateOrder,
            getPaymentMethods,
            getBanks,

            /** Payment methods */
            processSelectedPaymentMethod,
            updatePaymentForm,
            syncRecurringWithUpfront,
            clearPaymentForms,

            /** Terms and conditions */
            getTermsAndConditions,
            setAgreement,

            register,

            processPerformPayment,
            processCompleteOrder,
        };
    },
);
